import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { inr, MONEY_LINE, MONEY_GRAND_TOTAL } from "@/lib/format";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePOS } from "@/context/POSContext";
import { useActiveOrders } from "@/hooks/useActiveOrders";
import * as orderApi from "@/lib/orderApi";
import { toast } from "sonner";
import { registerUpdateBlocker } from "@/lib/versionWatch";
import { Plus, Minus, Search, Utensils, Receipt, CheckCircle, MenuIcon, ArrowLeft, Wallet, XCircle, StickyNote, Percent, X, ShoppingBag } from "lucide-react";
import { VariantModal, Modal, NumberPad } from "@/components/Modals";
import { fmtQty, roundQty } from "@/lib/qty";
import CancelItemDialog from "@/components/billing/CancelItemDialog";
import DiscountForm from "@/components/billing/DiscountForm";
import ItemNoteDialog from "@/components/orders/ItemNoteDialog";
import OrderActivity from "@/components/orders/OrderActivity";
import ItemDetailsDialog from "@/components/menu/ItemDetailsDialog";
import ItemContextMenu from "@/components/menu/ItemContextMenu";
import { useItemPress } from "@/hooks/useItemPress";
import { useNotePresets } from "@/lib/itemNotes";
import { uuid } from "@/lib/uuid";
import { tillCustomerSkipAllowed } from "@/lib/captainMesh/customerSkip";
import { createCustomer, getCustomer, listCustomers } from "@/lib/crmApi";
import GuestDetailsDialog from "@/components/billing/GuestDetailsDialog";
import OrderContextStrip, { OrderNoteDialog } from "@/components/billing/OrderContextStrip";

const serverReason = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || "Request rejected";
  return error?.message || "Could not reach the server";
};

// The bill's parts, as the bill itself states them (owner, 2026-10-24: the
// running table showed one amount). A preview names cgst/sgst; a frozen order
// carries them as cgst_amount/sgst_amount. Zero lines stay off the screen,
// except the three the owner asked for.
const BREAKDOWN = [
  ["Subtotal", r => r.subtotal, true],
  ["Discount", r => r.discount_amount, false],
  ["Service charge", r => r.service_charge, false],
  ["Other charges", r => r.other_charges, false],
  ["Loyalty redeemed", r => (r.loyalty_redeem_amount ? -Number(r.loyalty_redeem_amount) : 0), false],
  ["CGST", r => r.cgst ?? r.cgst_amount, true],
  ["SGST", r => r.sgst ?? r.sgst_amount, true],
  ["Round off", r => r.round_off ?? r.round_off_amount, false],
];

const billBreakdown = result =>
  BREAKDOWN
    .map(([label, pick, always]) => [label, Number(pick(result) ?? 0), always])
    .filter(([, value, always]) => always || value !== 0)
    .map(([label, value]) => [label, value]);

const NO_ADJUST = { discount: 0, discountPct: null, reason: null, serviceCharge: null };

// The preview/bill body for the cashier's choices: {} when there are none, so an
// untouched bill sends exactly what it always did.
const billArgs = adjust => ({
  ...(adjust.discountPct != null ? { discount: 0, discountPct: adjust.discountPct } : {}),
  ...(adjust.discountPct == null && adjust.discount > 0 ? { discount: adjust.discount, discountPct: null } : {}),
  ...(typeof adjust.serviceCharge === "boolean" ? { serviceCharge: adjust.serviceCharge } : {}),
});

export default function BillingTerminal() {
  const { tableId } = useParams(); const navigate = useNavigate(); const [searchParams] = useSearchParams();
  const { tables, menu, token, subscribeRealtime, permissions, paymentModes, settings, user } = usePOS(); const table = tables?.find(t => t.id === Number(tableId));
  // Which item is waiting on a size/pour choice. An item with variants has no
  // sellable price of its own — a 30ML and a BOTTLE are different money.
  const [variantItem, setVariantItem] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  
  // Realtime active orders
  const { orders, orderForTable, refresh: refreshActiveOrders, loaded: boardLoaded } = useActiveOrders(token, subscribeRealtime);
  const orderParam = searchParams.get("order"); const runningOrder = orderParam ? orders.find(o => o.id === Number(orderParam)) : (table ? orders.find(o => o.table_number === table.number && o.status !== "billing") : null);

  // Guests + customer (owner, 2026-10-24): asked when a table is opened, like the
  // phone; the customer only skippable when the setting allows. Before the first
  // KOT they ride /orders/start; on a running order the strip edits them.
  const canSkipCustomer = tillCustomerSkipAllowed(settings);
  const [startDetails, setStartDetails] = useState(null);     // { guests, customer } for a new table
  const [guestEditor, setGuestEditor] = useState(null);       // "start" | "edit" | null
  const [noteEditor, setNoteEditor] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);           // GET /orders/{id}: captain, guests, note, customer
  const [orderCustomer, setOrderCustomer] = useState(null);
  const [stripSaving, setStripSaving] = useState(false);
  const [stripError, setStripError] = useState(null);
  // The captains the PC may name on a new table (GET /orders/captains), read when
  // the guest step first opens.
  const [captains, setCaptains] = useState(null);
  const liveOrderId = runningOrder ? runningOrder.id || runningOrder.order_id : null;

  useEffect(() => {
    if (guestEditor !== "start" || captains !== null || !token) return undefined;
    let live = true;
    Promise.resolve()
      .then(() => orderApi.listOrderCaptains(token))
      .then(rows => { if (live) setCaptains(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (live) setCaptains([]); });
    return () => { live = false; };
  }, [guestEditor, captains, token]);

  // The route reuses this screen across tables: one table's guest is not the next's.
  useEffect(() => { setStartDetails(null); setGuestEditor(null); setNoteEditor(false); }, [tableId]);

  // A table with no order: ask before anything is punched. Waits for the board so a
  // busy table is never mistaken for an empty one.
  useEffect(() => {
    if (boardLoaded && table && !liveOrderId && !orderParam && !startDetails && guestEditor === null) setGuestEditor("start");
    if (liveOrderId && guestEditor === "start") setGuestEditor(null);
    // Used once the board shows the order: if it later ends (closed, cancelled
    // elsewhere) the next party starts from a blank guest step, not this one's.
    if (liveOrderId && startDetails) setStartDetails(null);
  }, [boardLoaded, table, liveOrderId, orderParam, startDetails, guestEditor]);

  useEffect(() => {
    if (!liveOrderId || !token) { setOrderInfo(null); setOrderCustomer(null); return undefined; }
    let live = true;
    // The strip is information: a failed read leaves it on the board row's values.
    Promise.resolve()
      .then(() => orderApi.getOrder(liveOrderId, token))
      .then(o => { if (live && o) setOrderInfo(o); })
      .catch(() => {});
    return () => { live = false; };
  }, [liveOrderId, runningOrder?.version, token]);

  useEffect(() => {
    const id = orderInfo?.customer_id;
    if (!id || !token) { setOrderCustomer(null); return undefined; }
    let live = true;
    getCustomer(id, token).then(c => { if (live) setOrderCustomer(c); }).catch(() => {});
    return () => { live = false; };
  }, [orderInfo?.customer_id, token]);

  const saveOrderPatch = async patch => {
    setStripSaving(true); setStripError(null);
    try {
      const fresh = await orderApi.getOrder(liveOrderId, token);
      const updated = await orderApi.updateOrder(liveOrderId, patch, fresh.version, token);
      setOrderInfo(updated);
      refreshActiveOrders();
      return true;
    } catch (e) {
      setStripError(serverReason(e));
      return false;
    } finally { setStripSaving(false); }
  };

  const saveGuestDetails = async ({ guests, customer, captainId }) => {
    if (guestEditor === "start") {
      setStartDetails({ guests, customer, captainId });
      setGuestEditor(null);
      return;
    }
    let ok = true;
    if (guests !== orderInfo?.guest_count) ok = await saveOrderPatch({ guest_count: guests });
    if (ok && customer && customer.id !== orderInfo?.customer_id) {
      setStripSaving(true); setStripError(null);
      try {
        let id = customer.id;
        if (id == null) {
          try {
            id = (await createCustomer({ name: customer.name, phone: customer.phone }, token)).id;
          } catch (createError) {
            // The mobile is already a customer (the dialog's lookup had not
            // answered yet): link that record instead of refusing.
            const rows = await listCustomers(customer.phone, token).catch(() => []);
            const existing = (rows || []).find(c => String(c.phone || "").replace(/\D/g, "").slice(-10) === customer.phone);
            if (!existing) throw createError;
            id = existing.id;
          }
        }
        await orderApi.linkOrderCustomer(liveOrderId, id, token);
        setOrderInfo(await orderApi.getOrder(liveOrderId, token));
      } catch (e) {
        setStripError(serverReason(e)); ok = false;
      } finally { setStripSaving(false); }
    }
    if (ok) setGuestEditor(null);
  };

  const saveOrderNote = async notes => {
    if (await saveOrderPatch({ notes: notes || null })) setNoteEditor(false);
  };
  
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The cart lives only here until a KOT or bill sends it: a new-version reload
  // must wait until it is empty.
  const unsavedRef = useRef(false);
  unsavedRef.current = cart.length > 0 || isSubmitting;
  useEffect(() => registerUpdateBlocker(() => unsavedRef.current), []);

  // Derive categories
  const categories = useMemo(() => {
    const cats = new Set(menu.map(i => i.category).filter(Boolean));
    return ["All", ...Array.from(cats)];
  }, [menu]);

  // Filter items
  const filteredItems = useMemo(() => {
    return menu.filter(item => {
      if (selectedCategory !== "All" && item.category !== selectedCategory) return false;
      if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase()) && !item.short_code?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [menu, selectedCategory, searchQuery]);

  // Existing order items
  const [existingItems, setExistingItems] = useState([]);
  // KOT number → { by, at }: who sent each KOT, as the kitchen slip names them.
  const [kotSenders, setKotSenders] = useState({});
  
  useEffect(() => {
    if (runningOrder && token) {
      orderApi.getOrderItems(runningOrder.id || runningOrder.order_id, token)
        .then(res => setExistingItems(res))
        .catch(err => console.error(err));
    } else {
      setExistingItems([]);
    }
  }, [runningOrder, token]);

  // A cart line is identified by item AND variation: a 30ML and a BOTTLE of the
  // same mocktail are two lines at two prices, not one line at quantity 2.
  const lineKeyOf = (menuItemId, variationId) => `${menuItemId}:${variationId ?? ""}`;

  // Special notes and item details (owner, 2026-10-20). Clicking a cart line opens
  // its note; long-press (touch) or right-click -> Show details (mouse) on a menu
  // card or a cart line opens the dish's details. One dialog at a time.
  const { presets: notePresets } = useNotePresets(token);
  const [noteFor, setNoteFor] = useState(null);       // { kind: "cart", cid, name, notes } | { kind: "saved", line }
  // The cart line whose quantity is being typed (NumberPad, with its ½ key):
  // the steppers only move whole plates, so the till could not punch a half
  // plate at all (owner, 2026-10-31).
  const [qtyFor, setQtyFor] = useState(null);         // { cid, name, qty }
  const [detailsFor, setDetailsFor] = useState(null); // a menu item
  const [contextFor, setContextFor] = useState(null); // { item, at }
  const press = useItemPress({
    onLongPress: item => setDetailsFor(item),
    onContextMenu: (item, at) => setContextFor({ item, at }),
  });
  const closeContext = useCallback(() => setContextFor(null), []);
  const menuItemFor = menuItemId => menu.find(m => String(m.id) === String(menuItemId)) || null;

  const addToCart = (item, variant) => {
    if (runningOrder?.status === "billing") {
      toast.error("Modifications are not allowed on billed orders.");
      return;
    }
    if (item.variants?.length && !variant) {
      setVariantItem(item);
      return;
    }
    const variationId = variant ? Number(variant.id) : null;
    // The variation's price IS the price when one is chosen. Billing the item's
    // base price for a bottle is the difference between ₹171 and ₹3,135.
    const unitPrice = Number(variant ? variant.price : item.price);
    const key = lineKeyOf(item.id, variationId);
    setCart(prev => {
      // A line with a note is its own line: a second dal punched after "extra
      // spicy" was written is a plain dal, not a third spicy one.
      const existing = prev.find(i => lineKeyOf(i.menu_item_id, i.variation_id) === key && !i.notes.trim());
      if (existing) {
        return prev.map(i =>
          i.cid === existing.cid
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, {
        cid: uuid(),
        menu_item_id: item.id,
        variation_id: variationId,
        variant_name: variant ? variant.name : null,
        unit_price: unitPrice,
        item,
        quantity: 1,
        notes: "",
      }];
    });
  };

  const updateCartQty = (cid, delta) => {
    if (runningOrder?.status === "billing") return;
    setCart(prev => prev.map(i => {
      if (i.cid === cid) {
        const newQty = Math.max(0, roundQty(i.quantity + delta));
        return { ...i, quantity: newQty };
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, i) => sum + ((i.unit_price ?? i.item.price) * i.quantity), 0);
  }, [cart]);
  
  // The board row's `amount` is the pre-tax running figure (live it read ₹520 for a
  // ₹546 bill). It is shown only as a labelled fallback when the server's own total
  // cannot be fetched.
  const runningTotal = runningOrder ? parseFloat(runningOrder.total ?? runningOrder.amount ?? 0) : 0;
  const runningOrderId = runningOrder ? runningOrder.id || runningOrder.order_id : null;
  const runningStatus = runningOrder?.status;
  const runningVersion = runningOrder?.version;

  // The cashier's choices for this bill (2026-11-01): a discount, and adding or
  // taking off the service charge when the outlet lets them (service_charge_apply).
  // They ride on the preview and on POST /bill; a different order starts clean.
  const [adjust, setAdjust] = useState(NO_ADJUST);
  const [discountOpen, setDiscountOpen] = useState(false);
  useEffect(() => { setAdjust(NO_ADJUST); }, [runningOrderId]);
  const previewArgs = useMemo(() => billArgs(adjust), [adjust]);
  const generateArgs = extra => ({
    ...previewArgs,
    ...(adjust.reason ? { discountReason: adjust.reason } : {}),
    ...extra,
  });
  const canDiscount = Array.isArray(permissions) && permissions.includes("billing.discount")
    && String(settings?.discount_enabled ?? "true").toLowerCase() !== "false";
  // Same defaults as billing_service (enabled unless the row says otherwise), and
  // shown only when there is a rate: production runs enabled at 0%.
  const serviceChargeAutomatic = String(settings?.service_charge_apply || "automatic") !== "per_bill";
  const serviceChargeOn = String(settings?.service_charge_enabled ?? "true").toLowerCase() === "true"
    && Number(settings?.service_charge_pct || 0) > 0
    // Taking an automatic charge off needs the discount permission (the server
    // refuses it without); adding a by-the-cashier one does not.
    && (canDiscount || !serviceChargeAutomatic);
  const serviceChargeApplied = adjust.serviceCharge ?? serviceChargeAutomatic;

  // What the bill actually comes to: the server preview (tax, service charge and
  // round-off included) while the order is running, its frozen total once billed.
  const [billTotal, setBillTotal] = useState({ phase: "idle" });
  useEffect(() => {
    if (!runningOrderId || !token) {
      setBillTotal({ phase: "idle" });
      return undefined;
    }
    const liveItems = (existingItems || []).filter(i => i.status !== "cancelled");
    if (!liveItems.length) {
      setBillTotal({ phase: "ready", total: 0 });
      return undefined;
    }
    let alive = true;
    // Never carry the last figure into a reload: the order on this table may have
    // changed underneath the screen, and a real number for the wrong order under
    // "Bill total" is worse than a moment of "…".
    setBillTotal({ phase: "loading" });
    const load = runningStatus === "billing"
      ? orderApi.getOrder(runningOrderId, token)
      : orderApi.previewBill(runningOrderId, previewArgs, token);
    load
      .then(result => { if (alive) setBillTotal({ phase: "ready", total: result.total, breakdown: billBreakdown(result), preview: result }); })
      .catch(err => { if (alive) setBillTotal({ phase: "error", reason: serverReason(err) }); });
    return () => { alive = false; };
    // existingItems.length: a KOT fired from this screen adds lines before the board
    // row's version is re-read.
  }, [runningOrderId, runningStatus, runningVersion, existingItems.length, token, previewArgs]);

  const hasUnsentExisting = useMemo(() => {
    return existingItems.some(ei => !ei.kot_number && ei.status !== "cancelled");
  }, [existingItems]);

  const handlePunchKOT = async () => {
    if (cart.length === 0 && !hasUnsentExisting) return;
    if (runningOrder?.status === "billing") { toast.error("Order is frozen at billing"); return; }
    // The customer step is not skippable here: a new table needs the guest first.
    if (!liveOrderId && !canSkipCustomer && !startDetails?.customer) {
      toast.error("Add the guest's name and mobile first");
      setGuestEditor("start");
      return;
    }
    setIsSubmitting(true);
    
    try {
      let orderId = runningOrder?.id || runningOrder?.order_id;
      let version = runningOrder?.version || 0;
      
      if (cart.length > 0) {
        // OrderItemCreate is extra="forbid": menu_item_id / quantity / notes and
        // the two optional id lists, nothing else. A stray item_name or
        // unit_price here is a 422 for the whole punch.
        const lines = cart.map(c => ({
          menu_item_id: c.menu_item_id,
          quantity: c.quantity,
          notes: c.notes.trim() || null,
          // The server prices the line from the variation, so omitting this bills
          // the base price for whatever size the guest actually ordered.
          ...(c.variation_id ? { variation_id: c.variation_id } : {}),
        }));

        if (!orderId) {
          // Atomic start: the order and its first lines commit in one
          // transaction. Creating the order first and adding lines in a second
          // call leaves an EMPTY order holding the table whenever that second
          // call fails — the phantom-order failure App.js documents (order 730,
          // 2026-08-16). /orders/start answers {order, items, customer, table}.
          const res = await orderApi.startOrder({
            table_id: Number(tableId),
            guest_count: startDetails?.guests ?? 1,
            order_type: "dine_in",
            ...(startDetails?.customer ? { customer: startDetails.customer } : {}),
            ...(startDetails?.captainId ? { captain_id: startDetails.captainId } : {}),
            first_items: lines
          }, token);
          orderId = res.order.id;
          version = res.order.version;
        } else {
          // /orders/{id}/items answers a LIST of lines, not the order, so it
          // carries no version. Reading `.version` off it yielded undefined,
          // and If-Match is Optional server-side — so the KOT fired with the
          // optimistic-locking guard silently switched off. Re-read the order
          // for the version the fire actually has to match.
          // Re-read first: a note saved on an unsent line moves the version, and
          // the board row may not have caught up yet.
          version = (await orderApi.getOrder(orderId, token)).version;
          await orderApi.addItems(orderId, lines, version, token);
          const fresh = await orderApi.getOrder(orderId, token);
          version = fresh.version;
        }
      } else {
        // Firing existing unsent items already on the order
        const fresh = await orderApi.getOrder(orderId, token);
        version = fresh.version;
      }

      await orderApi.fireKot(orderId, version, token);
      
      toast.success("KOT sent to kitchen!");
      setCart([]);
      refreshActiveOrders(); orderApi.getOrderItems(orderId, token).then(setExistingItems).catch(console.error);
    } catch (err) {
      // serverReason: a 422/409 detail is a list or an object, which a toast cannot render.
      toast.error(serverReason(err) || "Failed to send KOT");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Settle without printing (owner, 2026-09-17): close a running table straight to
  // payment and spend no bill paper. This screen is the billing PC's (/billing is
  // admin + cashier), and the server refuses print_bill=false from a captain, so
  // captain pads never get it. The bill is still frozen, numbered and snapshotted,
  // so it reprints from Bill History whenever a guest asks for one.
  const canSettle = Array.isArray(permissions) && permissions.includes("billing.settle");
  const settleModes = (paymentModes || []).filter(m => m.is_active !== false);
  const [settleFor, setSettleFor] = useState(null); // { id, order_number }
  const [settling, setSettling] = useState(false);
  // What to collect. The footer total is the board's pre-tax running amount, so
  // the dialog asks the server: the frozen total for a billed order, otherwise the
  // same preview the bill will be computed from. A failure is shown, not fatal —
  // the server computes the bill regardless, and billing must not stop on it.
  const [due, setDue] = useState({ phase: "idle" });

  const openSettle = async () => {
    const ref = { id: runningOrder.id || runningOrder.order_id, order_number: runningOrder.order_number };
    setSettleFor(ref);
    setDue({ phase: "loading" });
    try {
      const amount = runningOrder.status === "billing"
        ? (await orderApi.getOrder(ref.id, token)).total
        : (await orderApi.previewBill(ref.id, previewArgs, token)).total;
      setDue({ phase: "ready", total: amount });
    } catch (err) {
      setDue({ phase: "error", reason: serverReason(err) });
    }
  };

  // Print Bill: the same POST /orders/{id}/bill the billing board's quick action
  // fires, so it freezes the bill and releases the table exactly as the board does
  // (bible §39.2). The screen then stays on the billed order, where Settle Bill is.
  // A billed order gets Print Duplicate, the board's duplicate path, never a second
  // /bill: there is no billing → billing transition.
  const [printing, setPrinting] = useState(false);
  const billedOrderPath = id => `/billing/table/${tableId}?order=${id}`;

  const handlePrintBill = async () => {
    if (!runningOrderId || printing) return;
    const orderNumber = runningOrder.order_number;
    setPrinting(true);
    try {
      if (runningStatus === "billing") {
        await orderApi.printDuplicateBill(runningOrderId, token);
        toast.success(`Duplicate bill printing for #${orderNumber}`);
        return;
      }
      // Re-read: the board row's version can be stale, and another terminal may
      // have billed this order since the board last refreshed.
      const fresh = await orderApi.getOrder(runningOrderId, token);
      if (fresh.status === "billing") {
        toast.error(`#${orderNumber} was already billed on another terminal — use Print Duplicate for another copy`);
        refreshActiveOrders();
        navigate(billedOrderPath(runningOrderId), { replace: true });
        return;
      }
      const result = await orderApi.generateBill(runningOrderId, generateArgs(), fresh.version, token);
      refreshActiveOrders();
      navigate(billedOrderPath(runningOrderId), { replace: true });
      if (result?.print_job) {
        toast.success(`Bill printed for #${orderNumber} — table released; settle it here or from Pending Bills`);
      } else {
        toast.warning(`Bill for #${orderNumber} is saved but nothing printed — auto-print is off or no bill printer is set`);
      }
    } catch (err) {
      toast.error(`Could not print the bill for #${orderNumber}: ${serverReason(err)}`);
    } finally {
      setPrinting(false);
    }
  };

  const settleWithoutPrinting = async mode => {
    if (!settleFor || settling || printing) return;
    setSettling(true);
    let billFrozen = false;
    try {
      // Re-read first: the board row's version can be stale, and an earlier attempt
      // may already have frozen the bill. A frozen bill is closed, never billed again
      // — there is no billing → billing transition, so a second /bill is a 400.
      let fresh = await orderApi.getOrder(settleFor.id, token);
      if (fresh.status === "billing") {
        billFrozen = true;
      } else {
        await orderApi.generateBill(settleFor.id, generateArgs({ printBill: false }), fresh.version, token);
        billFrozen = true;
        // The bill bumps the version; close carries If-Match.
        fresh = await orderApi.getOrder(settleFor.id, token);
      }
      await orderApi.closeOrderOnServer(settleFor.id, mode.name, fresh.version, token);
      toast.success(`#${settleFor.order_number} settled · ${mode.display_label || mode.name} · no bill printed`);
      setSettleFor(null);
      refreshActiveOrders();
      navigate("/billing");
    } catch (err) {
      // Past the freeze the table is already released and the bill sits in Pending
      // Bills; the dialog stays open so choosing the payment again finishes it.
      toast.error(
        billFrozen
          ? `Bill #${settleFor.order_number} is saved but not settled: ${serverReason(err)}. Choose the payment again to settle it.`
          : `Could not settle #${settleFor.order_number} — not settled: ${serverReason(err)}`
      );
    } finally {
      setSettling(false);
    }
  };

  // One row per order line, never merged: each row carries its own Cancel, and a
  // merged row hid which line a cancel would hit. Cancelled lines stay listed,
  // struck through, so staff see what the kitchen was told to stop — before, they
  // were summed into the live quantity and money, so a voided line still read as
  // ordered.
  const groupedExistingItemsByKOT = useMemo(() => {
    const kots = {};
    existingItems.forEach(ei => {
      const kotNum = ei.kot_number || 0;
      if (!kots[kotNum]) kots[kotNum] = [];
      kots[kotNum].push({ ...ei, quantity: parseFloat(ei.quantity) });
    });
    return Object.entries(kots)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([kotNum, items]) => ({
        kot_number: Number(kotNum),
        items: items.sort((a, b) => (a.status === "cancelled") - (b.status === "cancelled") || a.id - b.id),
      }));
  }, [existingItems]);

  const saveNote = async note => {
    if (noteFor.kind === "cart") {
      setCart(prev => prev.map(i => (i.cid === noteFor.cid ? { ...i, notes: note } : i)));
      return true;
    }
    // A saved line the kitchen has not been sent (punched on a pad, not fired):
    // the note goes to the server, which refuses it once the line is on a KOT.
    try {
      const fresh = await orderApi.getOrder(runningOrderId, token);
      await orderApi.setItemNote(runningOrderId, noteFor.line.id, note, fresh.version, token);
    } catch (err) {
      throw new Error(`The note was not saved: ${serverReason(err)}`);
    }
    toast.success(note ? `Note saved for ${noteFor.line.item_name}` : `Note removed from ${noteFor.line.item_name}`);
    // The note moved the order's version: re-read the board so the next punch
    // carries the new If-Match instead of failing on the old one.
    refreshActiveOrders();
    orderApi.getOrderItems(runningOrderId, token).then(setExistingItems).catch(console.error);
    return true;
  };

  // Cancel Item: one line, some or all of its quantity (CancelItemDialog). The
  // whole-table cancel stays on the tables board, behind its manager PIN.
  const [voidLine, setVoidLine] = useState(null);
  const searchInputRef = useRef(null);

  // Billing keyboard shortcuts (DESIGN.md §3, V15): F2 (KOT), F4 (Settle), F6 (Bill), F8 (Tables), / (Search), Esc
  useEffect(() => {
    const handleKeyDown = e => {
      if (cartDrawerOpen) {
        if (e.key === "Escape") {
          e.preventDefault();
          setCartDrawerOpen(false);
        }
        return;
      }

      // Don't intercept when a modal dialog is open
      if (document.querySelector('[role="dialog"]')) return;

      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInput = activeTag === "input" || activeTag === "textarea" || activeTag === "select";

      if (e.key === "/" && !isInput) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (e.key === "Escape") {
        if (searchQuery) {
          e.preventDefault();
          setSearchQuery("");
        } else if (!isInput) {
          e.preventDefault();
          navigate("/billing");
        }
        return;
      }

      if (e.key === "F2") {
        e.preventDefault();
        if (cart.length > 0 && !isSubmitting && runningOrder?.status !== "billing") {
          handlePunchKOT();
        }
        return;
      }

      if (e.key === "F4") {
        e.preventDefault();
        if (canSettle && runningOrder && cart.length === 0 && !settling && !printing) {
          openSettle();
        }
        return;
      }

      if (e.key === "F6") {
        e.preventDefault();
        if (runningOrder && cart.length === 0 && !printing && !settling) {
          handlePrintBill();
        }
        return;
      }

      if (e.key === "F8") {
        e.preventDefault();
        navigate("/billing");
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    cart.length,
    isSubmitting,
    runningOrder,
    canSettle,
    settling,
    printing,
    searchQuery,
    navigate,
    handlePunchKOT,
    openSettle,
    handlePrintBill,
    cartDrawerOpen,
  ]);

  const renderCartPanel = (isDrawer = false) => (
    <div className="flex-1 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 flex flex-col gap-4">
        {/* Existing Order Items */}
        {groupedExistingItemsByKOT.length > 0 && (
          <div className="mb-2 flex flex-col gap-3">
            {groupedExistingItemsByKOT.map(kotGroup => (
              <div key={kotGroup.kot_number}>
                <h3 className="text-micro font-bold text-muted-foreground mb-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> {kotGroup.kot_number ? `KOT #${kotGroup.kot_number}` : "Unsent (Saved)"}
                  {kotGroup.kot_number && kotSenders[kotGroup.kot_number]?.by ? (
                    <span className="font-normal" data-testid="kot-sender">· {kotSenders[kotGroup.kot_number].by}</span>
                  ) : null}
                </h3>
                <div className="flex flex-col">
                  {kotGroup.items.map(ei => {
                    const cancelled = ei.status === "cancelled";
                    const noteable = !cancelled && !ei.kot_number && runningStatus !== "billing";
                    const linkedItem = menuItemFor(ei.menu_item_id);
                    return (
                      <div key={ei.id} className={`flex justify-between items-center gap-2 py-0.5 text-body ${cancelled ? "opacity-60" : ""}`} data-testid={cancelled ? "existing-line-cancelled" : "existing-line"}>
                        <div
                          className={`flex-1 min-w-0 select-none ${noteable ? "cursor-pointer" : ""}`}
                          {...(linkedItem ? press.bind(linkedItem) : {})}
                          onClick={noteable ? () => setNoteFor({ kind: "saved", line: ei }) : undefined}
                          title={noteable ? "Click to add or change the note" : undefined}
                          data-testid="existing-line-body"
                        >
                          <span className={`font-semibold ${cancelled ? "line-through" : ""}`}>{ei.item_name}{ei.variation_name ? ` · ${ei.variation_name}` : ""}</span>
                          {cancelled && <span className="ml-2 text-micro font-bold text-destructive">Cancelled</span>}
                          {ei.notes && <div className="text-meta text-amber-700 flex items-center gap-1"><StickyNote className="w-3 h-3 shrink-0" />{ei.notes}</div>}
                        </div>
                        <div className="flex gap-2 items-center shrink-0">
                          <span className={`text-meta text-muted-foreground tabular-nums ${cancelled ? "line-through" : ""}`}>x{ei.quantity}</span>
                          <span className={`${MONEY_LINE} min-w-[4.5rem] text-right ${cancelled ? "line-through" : ""}`}>{inr(ei.unit_price * ei.quantity)}</span>
                          {!cancelled && runningStatus !== "billing" && (
                            <button
                              className="w-8 h-8 !min-h-0 -mr-1 flex items-center justify-center rounded-md text-danger hover:bg-danger-subtle"
                              onClick={() => setVoidLine(ei)}
                              aria-label={`Cancel ${ei.item_name}`}
                              title="Cancel this item"
                              data-testid="cancel-item-button"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {runningOrderId && (
          <OrderActivity
            orderId={runningOrderId}
            token={token}
            refreshKey={`${runningOrder?.version}-${runningOrder?.status}-${existingItems.length}`}
            onKots={setKotSenders}
            className="border-t pt-2"
          />
        )}

        {/* Unsent Cart Items */}
        {cart.length > 0 && (
          <div>
            <h3 className="text-micro font-bold text-primary mb-2 flex items-center gap-1">
              <Receipt className="w-3 h-3" /> New KOT Items
            </h3>
            <div className="flex flex-col gap-3">
              {cart.map(c => {
                const name = `${c.item.name}${c.variant_name ? ` · ${c.variant_name}` : ""}`;
                const openCartNote = () => setNoteFor({ kind: "cart", cid: c.cid, name, notes: c.notes });
                return (
                <div key={c.cid} className="flex flex-col gap-1 bg-muted/30 p-2 rounded-md animate-slide-in-right" data-testid="cart-line">
                  <div
                    className="flex justify-between items-start cursor-pointer select-none"
                    {...press.bind(c.item)}
                    onClick={openCartNote}
                    title="Click to add a note for the kitchen"
                    data-testid="cart-line-body"
                  >
                    <span className="font-semibold text-body flex-1 pr-2">{name}</span>
                    <span className={`${MONEY_LINE} w-12 text-right`}>{inr((c.unit_price ?? c.item.price) * c.quantity)}</span>
                  </div>
                  {c.notes.trim() ? (
                    <div className="text-meta text-amber-700 flex items-center gap-1" data-testid="cart-line-note"><StickyNote className="w-3 h-3 shrink-0" />{c.notes}</div>
                  ) : null}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3 bg-background border rounded-md">
                      <button onClick={() => updateCartQty(c.cid, -1)} className="px-2 py-1 hover:bg-muted rounded-l-md" aria-label={`One less ${name}`}><Minus className="w-3 h-3" /></button>
                      <button
                        type="button"
                        onClick={() => runningOrder?.status !== "billing" && setQtyFor({ cid: c.cid, name, qty: c.quantity })}
                        className="text-body font-bold min-w-[2rem] px-1 py-1 text-center tabular-nums hover:bg-muted rounded-sm"
                        aria-label={`Set quantity for ${name}`}
                        title="Click to type a quantity or a half plate"
                        data-testid="cart-line-qty-button"
                      >
                        {fmtQty(c.quantity)}
                      </button>
                      <button onClick={() => updateCartQty(c.cid, 1)} className="px-2 py-1 hover:bg-muted rounded-r-md" aria-label={`One more ${name}`}><Plus className="w-3 h-3" /></button>
                    </div>
                    <button
                      type="button"
                      className="text-micro font-semibold text-muted-foreground hover:text-foreground px-2 py-1"
                      onClick={openCartNote}
                      data-testid="cart-line-note-button"
                    >
                      {c.notes.trim() ? "Edit note" : "+ Note"}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
        
        {cart.length === 0 && existingItems.length === 0 && (
          <div className="flex-1 flex flex-col justify-center items-center text-muted-foreground h-full opacity-50">
            <Utensils className="w-12 h-12 mb-4" />
            <p className="text-title font-bold">Cart is empty</p>
            <p className="text-meta">Click items on the left to add</p>
          </div>
        )}
      </div>
      
      {/* Payment & Actions Footer */}
      <div className="bg-muted/10 border-t p-3 sm:p-4 shrink-0">
        <div className="mb-4">
          {runningOrder ? (
            <>
            {billTotal.phase === "ready" && billTotal.breakdown?.length > 0 && (
              <div className="mb-1 space-y-0.5 text-body" data-testid="footer-bill-breakdown">
                {billTotal.breakdown.map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-muted-foreground">{label}</span>
                    <span className={MONEY_LINE}>{inr(value)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center text-lede" data-testid="footer-bill-total">
              {billTotal.phase === "error" ? (
                <>
                  <span className="font-bold text-muted-foreground">Subtotal (before tax)</span>
                  <span className={MONEY_GRAND_TOTAL}>{inr(runningTotal)}</span>
                </>
              ) : (
                <>
                  <span className="font-bold text-muted-foreground">Bill total (incl. tax)</span>
                  <span className={MONEY_GRAND_TOTAL}>{billTotal.phase === "ready" ? inr(billTotal.total) : "…"}</span>
                </>
              )}
            </div>
            </>
          ) : cart.length === 0 && (
            <div className="flex justify-between items-center text-lede">
              <span className="font-bold text-muted-foreground">Total</span>
              <span className={MONEY_GRAND_TOTAL}>{inr(0)}</span>
            </div>
          )}
          {runningOrder && billTotal.phase === "error" && (
            <p className="text-meta text-destructive" data-testid="footer-total-error">Could not get the bill total: {billTotal.reason}</p>
          )}
          {cart.length > 0 && (
            <div className="flex justify-between items-center text-body mt-1" data-testid="footer-new-items">
              <span className="font-semibold text-muted-foreground">New items (before tax)</span>
              <span className={MONEY_LINE}>{inr(cartTotal)}</span>
            </div>
          )}
        </div>

        {runningOrder && runningStatus !== "billing" && (canDiscount || serviceChargeOn) && (
          <div className="grid grid-cols-2 gap-2.5 mb-2.5" data-testid="bill-adjustments">
            {canDiscount && (
              <button
                className="h-11 rounded-md border border-border bg-card hover:bg-muted text-body font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                onClick={() => setDiscountOpen(true)}
                disabled={printing || settling}
                data-testid="bill-discount-button"
              >
                <Percent className="w-4 h-4" />
                <span>{adjust.discountPct != null ? `Discount ${adjust.discountPct}%`
                  : adjust.discount > 0 ? `Discount ${inr(adjust.discount)}` : "Discount"}</span>
              </button>
            )}
            {serviceChargeOn && (
              <button
                className={`h-11 rounded-md border text-body font-medium disabled:opacity-50 ${serviceChargeApplied ? "border-action bg-action-subtle text-action" : "border-border bg-card hover:bg-muted"}`}
                onClick={() => setAdjust(a => ({ ...a, serviceCharge: !serviceChargeApplied }))}
                disabled={printing || settling}
                aria-pressed={serviceChargeApplied}
                data-testid="bill-service-charge-button"
              >
                {serviceChargeApplied ? "Service charge on" : "Add service charge"}
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <button
            className="bg-secondary text-secondary-foreground hover:bg-secondary/80 h-11 rounded-md font-medium text-body border border-border transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-1.5"
            onClick={handlePrintBill}
            disabled={!runningOrder || cart.length > 0 || printing || settling}
            title={runningOrder ? undefined : "Nothing to bill yet — send a KOT for this table first"}
            data-testid="print-bill-button"
          >
            <span>{printing
              ? "Printing…"
              : runningOrder?.status === "billing" ? "Print Duplicate" : "Print Bill"}</span>
            <kbd className="px-1 py-0.2 bg-muted text-muted-foreground rounded text-[10px] font-mono border [@media(pointer:coarse)]:hidden">F6</kbd>
          </button>
          <button
            className="bg-action text-action-foreground hover:bg-action/90 h-11 rounded-md font-medium text-body transition-all active:scale-95 disabled:bg-muted disabled:text-muted-foreground disabled:border disabled:border-border disabled:active:scale-100 flex items-center justify-center gap-1.5"
            onClick={handlePunchKOT}
            disabled={(!cart.length && !hasUnsentExisting) || isSubmitting || runningOrder?.status === "billing"}
            data-testid="punch-kot-button"
          >
            <span>{isSubmitting ? "Saving..." : (cart.length > 0 ? "Save & Print KOT" : "Fire KOT")}</span>
            <kbd className="px-1 py-0.2 bg-white/20 rounded text-[10px] font-mono [@media(pointer:coarse)]:hidden">F2</kbd>
          </button>
        </div>

        {canSettle && runningOrder && (
          <>
            <button
              className="mt-2.5 w-full bg-action text-action-foreground hover:bg-action/90 h-11 rounded-md font-medium text-body transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:active:scale-100"
              onClick={openSettle}
              disabled={cart.length > 0 || settling || printing}
              data-testid="settle-no-print-button"
            >
              <Wallet className="w-4 h-4" />
              <span>{runningOrder.status === "billing" ? "Settle Bill" : "Settle without printing"}</span>
              <kbd className="ml-1 px-1 py-0.2 bg-white/20 rounded text-[10px] font-mono [@media(pointer:coarse)]:hidden">F4</kbd>
            </button>
          </>
        )}
        {runningOrder && cart.length > 0 && (
          <p className="mt-1 text-meta text-muted-foreground text-center">Send the new items to the kitchen before printing the bill or settling.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden animate-slide-up">
      {/* HEADER: Standard shell header (DESIGN.md §4: One shell, no solid-color bar) */}
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-4 bg-card text-foreground border-b sticky top-0 z-20 shrink-0 gap-3">
        <div className="flex items-center gap-1.5">
          {/* Sidebar / Drawer menu toggle button */}
          <button
            className="w-11 h-11 -ml-2 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors shrink-0"
            onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
            aria-label="Toggle menu"
            data-testid="billing-terminal-menu-button"
          >
            <MenuIcon className="w-5 h-5" />
          </button>

          {/* Breadcrumb back to tables */}
          <button
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground text-meta font-medium transition-colors"
            onClick={() => navigate("/billing")}
            aria-label="Back to tables"
            title="Back to tables (Esc or F8)"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Tables</span>
            <span className="text-muted-foreground/50">›</span>
          </button>
          
          <div className="flex items-center gap-2 ml-1">
            <h1 className="text-lede font-semibold text-foreground leading-tight">{table?.name || "New Order"}</h1>
            <span className={`text-micro font-medium px-2 py-0.5 rounded-md ${
              runningOrder?.status === "billing"
                ? "bg-warning-subtle text-warning border border-warning/30"
                : "bg-info-subtle text-info border border-info/30"
            }`}>
              {runningOrder?.status === "billing" ? "Bill Printed" : "Punching"}
            </span>
            {/* The bill's own number, as on its card (owner, 2026-10-30). */}
            {runningOrder?.bill_number != null && (
              <span className="text-meta font-semibold text-foreground tabular-nums" data-testid="terminal-bill-number">
                Bill {runningOrder.bill_number}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Key hints only where there is a keyboard-and-mouse till: a touch tablet has no F-keys. */}
          <div className="hidden sm:flex [@media(pointer:coarse)]:hidden items-center gap-2 text-micro text-muted-foreground">
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded border font-mono text-[10px]">F2</kbd> KOT</span>
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded border font-mono text-[10px]">F6</kbd> Bill</span>
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded border font-mono text-[10px]">F4</kbd> Settle</span>
            <span><kbd className="px-1.5 py-0.5 bg-muted rounded border font-mono text-[10px]">/</kbd> Search</span>
          </div>
          <span className="text-lede font-semibold tracking-tight hidden md:inline text-muted-foreground/70">RestoYaar</span>
        </div>
      </header>

      {/* Full width under the header: in the 380px order pane each slot was ~75px and every name was cut (2026-10-24). */}
      <OrderContextStrip
        tableName={table?.name}
        captainName={orderInfo?.captain_name ?? runningOrder?.captain_name ?? (liveOrderId ? null : (captains?.find(c => c.id === startDetails?.captainId)?.name ?? user?.name))}
        guests={orderInfo?.guest_count ?? runningOrder?.guest_count ?? startDetails?.guests}
        note={orderInfo?.notes}
        customerName={orderCustomer?.name ?? (liveOrderId ? null : startDetails?.customer?.name)}
        readOnly={runningOrder?.status === "billing"}
        onGuests={() => { setStripError(null); setGuestEditor(liveOrderId ? "edit" : "start"); }}
        onCustomer={() => { setStripError(null); setGuestEditor(liveOrderId ? "edit" : "start"); }}
        onNote={liveOrderId ? () => { setStripError(null); setNoteEditor(true); } : undefined}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* 1. Left Pane: Categories (DESIGN.md §4.1: ≤200px, 44px touch rows) */}
        <div className="w-[85px] sm:w-[140px] lg:w-[200px] bg-card border-r border-border flex flex-col shrink-0">
          <div className="px-2 sm:px-3 py-3 border-b text-meta font-medium text-muted-foreground flex items-center justify-between">
            <span className="truncate">Categories</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {categories.map(cat => {
              const isSelected = selectedCategory === cat;
              return (
                <button 
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`w-full text-left px-2 sm:px-3 min-h-[44px] text-meta sm:text-body font-medium border-b border-border/40 transition-colors flex items-center gap-1.5 sm:gap-2 ${
                    isSelected ? "bg-muted text-primary font-semibold border-l-2 border-l-primary" : "text-foreground hover:bg-muted/60"
                  }`}
                  data-testid={`category-pill-${cat}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSelected ? "bg-primary" : "bg-muted-foreground/30"}`}></span>
                  <span className="truncate">{cat}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* 2. Middle Pane: Items */}
        <div className="flex-1 bg-muted/10 flex flex-col min-w-0 relative">
          {runningOrder?.status === "billing" && (
            <div className="absolute inset-0 z-50 bg-background/50 backdrop-blur-[2px] flex flex-col items-center justify-center">
              <div className="bg-card p-6 rounded-xl shadow-md border border-border flex flex-col items-center max-w-sm text-center">
                <Receipt className="w-10 h-10 text-muted-foreground mb-3" />
                <h3 className="text-lede font-semibold mb-1">Order Billed</h3>
                <p className="text-meta text-muted-foreground">Modifications are not allowed after the bill is printed. Settle the payment to close the order.</p>
              </div>
            </div>
          )}
          <div className="p-3 border-b bg-card flex gap-3 shrink-0 shadow-sm z-10">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <input 
                ref={searchInputRef}
                type="text" 
                placeholder="Search item name or short code... (Press /)" 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full h-10 rounded-md border pl-9 pr-3 text-body bg-background focus:ring-[3px] focus:ring-primary/15 focus:border-primary outline-none transition-all" 
              />
            </div>
          </div>
          
          <div className="flex-1 p-3 overflow-y-auto">
            {/* DESIGN.md §4.1: container-based auto-fill grid */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,8rem),1fr))] gap-2 sm:gap-2.5 pb-24 lg:pb-20">
              {filteredItems.map((item, i) => (
                <button 
                  key={item.id}
                  onClick={() => addToCart(item)}
                  {...press.bind(item)}
                  className="bg-card hover:border-primary/50 border border-border rounded-lg p-3 text-left flex flex-col justify-between min-h-[5.5rem] transition-all active:scale-95 animate-pop select-none"
                  style={{ animationDelay: `${i * 12}ms`, WebkitTouchCallout: "none" }}
                  data-testid={`item-card-${item.id}`}
                >
                  <div className="text-body font-medium leading-snug line-clamp-2 min-h-[2.5rem] break-words">{item.name}</div>
                  <div className="flex justify-between items-center mt-2 w-full pt-1 border-t border-border/40">
                    <span className={`${MONEY_LINE} text-primary font-semibold text-meta`}>{inr(item.price)}</span>
                    {item.short_code && <span className="text-micro bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{item.short_code}</span>}
                  </div>
                </button>
              ))}
              {filteredItems.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center p-12 text-muted-foreground">
                  <Utensils className="w-10 h-10 mb-3 opacity-20" />
                  <p className="text-meta">No items found in this category.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* 3. Right Pane: Cart & Bill (Desktop >= 1024px) */}
        <div className="hidden lg:flex w-[320px] lg:w-[380px] xl:w-[400px] bg-card border-l border-border flex-col shrink-0 z-20 shadow-sm">
          {renderCartPanel(false)}
        </div>
      </div>

      {/* Sticky Bottom Bar for Tablet & Mobile (< 1024px) */}
      <div className="lg:hidden shrink-0 border-t bg-card p-2 px-3 flex items-center justify-between gap-2 shadow-lg z-20" data-testid="mobile-cart-bar">
        <button
          type="button"
          onClick={() => setCartDrawerOpen(true)}
          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-muted text-left min-w-0 flex-1 transition-colors"
          data-testid="mobile-cart-toggle"
          aria-label="Open cart drawer"
        >
          <div className="relative w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ShoppingBag className="w-4 h-4" />
            {(cart.length + existingItems.length) > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {cart.length + existingItems.length}
              </span>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-baseline gap-1.5">
              <span className="text-micro font-medium text-muted-foreground">Total:</span>
              <span className={`${MONEY_LINE} text-body font-bold text-foreground`}>
                {runningOrder
                  ? (billTotal.phase === "ready" ? inr(billTotal.total) : inr(runningTotal + cartTotal))
                  : inr(cartTotal)}
              </span>
            </div>
            <span className="text-micro text-primary font-medium flex items-center gap-0.5">
              View Cart & Bill ›
            </span>
          </div>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          {cart.length > 0 ? (
            <button
              type="button"
              className="bg-action text-action-foreground hover:bg-action/90 h-10 px-3.5 rounded-md font-medium text-body transition-all active:scale-95 disabled:opacity-50 flex items-center gap-1.5"
              onClick={handlePunchKOT}
              disabled={isSubmitting || runningOrder?.status === "billing"}
              data-testid="mobile-punch-kot-button"
            >
              <span>{isSubmitting ? "Saving…" : "Punch KOT"}</span>
            </button>
          ) : canSettle && runningOrder ? (
            <button
              type="button"
              className="bg-action text-action-foreground hover:bg-action/90 h-10 px-3.5 rounded-md font-medium text-body transition-all active:scale-95 flex items-center gap-1.5 disabled:opacity-50"
              onClick={openSettle}
              disabled={settling || printing}
              data-testid="mobile-settle-button"
            >
              <Wallet className="w-3.5 h-3.5" />
              <span>Settle</span>
            </button>
          ) : runningOrder ? (
            <button
              type="button"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 px-3.5 rounded-md font-medium text-body border transition-all active:scale-95 disabled:opacity-50 flex items-center gap-1.5"
              onClick={handlePrintBill}
              disabled={printing || settling}
              data-testid="mobile-print-bill-button"
            >
              <span>{printing ? "Printing…" : "Bill"}</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Cart Drawer for Tablet & Mobile (< 1024px) */}
      {cartDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end lg:hidden animate-in fade-in duration-150" role="dialog" aria-modal="true" aria-label="Cart and billing panel" data-testid="mobile-cart-drawer">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-xs" onClick={() => setCartDrawerOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-card shadow-2xl flex flex-col z-10 animate-in slide-in-from-right duration-200">
            <div className="h-12 px-4 border-b flex items-center justify-between shrink-0 bg-card">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-primary" />
                <span className="font-semibold text-body">Order & Cart</span>
                <span className="text-micro text-muted-foreground">({cart.length + existingItems.length} items)</span>
              </div>
              <button
                onClick={() => setCartDrawerOpen(false)}
                className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
                aria-label="Close cart"
                data-testid="close-cart-drawer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {renderCartPanel(true)}
            </div>
          </div>
        </div>
      )}

      {discountOpen && runningOrder && (
        <Modal title="Discount" onClose={() => setDiscountOpen(false)} testId="bill-discount-modal">
          <DiscountForm
            order={{ id: runningOrderId, orderNumber: runningOrder.order_number }}
            preview={billTotal.preview}
            settings={settings}
            discount={adjust}
            onApply={(next, reason) => setAdjust(a => ({ ...a, ...next, reason }))}
            onClear={() => setAdjust(a => ({ ...a, discount: 0, discountPct: null, reason: null }))}
            onDone={() => setDiscountOpen(false)}
          />
        </Modal>
      )}
      {settleFor && (
        <Modal
          title={`Settle #${settleFor.order_number} · no bill printed`}
          onClose={() => { if (!settling) setSettleFor(null); }}
          testId="settle-no-print-modal"
        >
          <div className="p-4 space-y-2">
            <div className="flex items-baseline justify-between pb-2" data-testid="settle-no-print-due">
              {due.phase === "error" ? (
                <span className="text-meta text-destructive">Could not work out the amount due: {due.reason}</span>
              ) : (
                <>
                  <span className="text-meta font-bold text-muted-foreground">Amount due</span>
                  <span className={MONEY_GRAND_TOTAL}>{due.phase === "ready" ? inr(due.total) : "…"}</span>
                </>
              )}
            </div>
            <p className="text-micro font-bold text-muted-foreground mb-1">Choose payment method</p>
            {settleModes.map(mode => (
              <button
                key={mode.id ?? mode.name}
                className="w-full h-14 px-4 text-body font-bold text-left rounded-xl border bg-background hover:bg-muted flex items-center gap-3 disabled:opacity-50"
                disabled={settling}
                onClick={() => settleWithoutPrinting(mode)}
                data-testid={`settle-no-print-mode-${mode.name}`}
              >
                <Wallet className="w-4 h-4" />
                {mode.display_label || mode.name}
              </button>
            ))}
            {!settleModes.length && (
              <p className="text-center text-meta text-muted-foreground py-4">No active payment modes are configured — ask the admin to add one.</p>
            )}
            <p className="text-meta text-muted-foreground pt-1">The bill is saved and can be reprinted from Bill History.</p>
          </div>
        </Modal>
      )}

      {qtyFor && (
        <NumberPad
          item={{ name: qtyFor.name, qty: qtyFor.qty }}
          onClose={() => setQtyFor(null)}
          onSave={qty => {
            setCart(prev => prev.map(i => (i.cid === qtyFor.cid ? { ...i, quantity: roundQty(qty) } : i)));
            setQtyFor(null);
          }}
        />
      )}

      {voidLine && (
        <CancelItemDialog
          orderId={runningOrderId}
          line={voidLine}
          token={token}
          onDone={(items, { orderCancelled } = {}) => {
            setVoidLine(null);
            refreshActiveOrders();
            if (orderCancelled) return navigate("/billing");
            if (items) setExistingItems(items);
          }}
          onClose={() => setVoidLine(null)}
        />
      )}

      {noteFor && (
        <ItemNoteDialog
          key={noteFor.kind === "cart" ? noteFor.cid : noteFor.line.id}
          itemName={noteFor.kind === "cart" ? noteFor.name : noteFor.line.item_name}
          initial={noteFor.kind === "cart" ? noteFor.notes : noteFor.line.notes || ""}
          presets={notePresets}
          onSave={saveNote}
          onClose={() => setNoteFor(null)}
        />
      )}

      {detailsFor && (
        <ItemDetailsDialog item={detailsFor} variant="modal" onClose={() => setDetailsFor(null)} />
      )}

      {contextFor && (
        <ItemContextMenu
          at={contextFor.at}
          onShowDetails={() => setDetailsFor(contextFor.item)}
          onClose={closeContext}
        />
      )}

      {variantItem && (
        <VariantModal
          item={variantItem}
          onSelect={variant => { const it = variantItem; setVariantItem(null); addToCart(it, variant); }}
          onClose={() => setVariantItem(null)}
        />
      )}

      {guestEditor && (
        <GuestDetailsDialog
          key={`${guestEditor}-${liveOrderId || "new"}`}
          title={guestEditor === "start" ? `${table?.name || "Table"} — guests` : "Guests & customer"}
          initial={guestEditor === "start"
            ? startDetails
            : { guests: orderInfo?.guest_count ?? runningOrder?.guest_count, customer: orderCustomer && { id: orderCustomer.id, name: orderCustomer.name, phone: orderCustomer.phone } }}
          canSkip={canSkipCustomer}
          token={token}
          saving={stripSaving}
          error={stripError}
          onSave={saveGuestDetails}
          onSkip={guestEditor === "start" ? ({ guests, captainId }) => { setStartDetails({ guests, customer: null, captainId }); setGuestEditor(null); } : undefined}
          captains={guestEditor === "start" ? captains || [] : undefined}
          selfName={user?.name}
          onCancel={() => { if (guestEditor === "start" && !startDetails) navigate("/billing"); else setGuestEditor(null); }}
        />
      )}

      {noteEditor && (
        <OrderNoteDialog
          initial={orderInfo?.notes}
          saving={stripSaving}
          error={stripError}
          onSave={saveOrderNote}
          onCancel={() => setNoteEditor(false)}
        />
      )}
    </div>
  );
}

